import NoData from "@rio-cloud/rio-uikit/NoData";
import DummyRowDropdown from "./DummyRowDropdown";
import SharedTable, { resolveDisplaySortKey } from "../SharedTable";
import type { SharedTableBaseProps, SharedTableSelection, SharedVehicleRow } from "../SharedTable";
import SharedCategoryCell from "../SharedCategoryCell";
import { buildTripDetailsUrl, openTripDetails } from "@utils/routingDeepLink";
import { renderDriverContent } from "../SharedDriverCell";
import { useT } from "@hooks";

type SortKey<T> = keyof T | [keyof T, keyof T] | null;

export type ExpenseStatus = "previsto" | "realizado" | "vencido";

export type Vehicle = SharedVehicleRow<ExpenseStatus>;

const STATUS_BADGE_CLASS: Record<ExpenseStatus, string> = {
  previsto: "badge bg-lightest border border-color-lighter",
  realizado: "badge badge-soft-success",
  vencido: "badge badge-soft-danger",
};

interface Props extends SharedTableBaseProps<Vehicle, SortKey<Vehicle>> {
  selection?: SharedTableSelection<Vehicle>;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onGetFile?: (id: string, category: string) => void;
  onDeleteFile?: (id: string) => void;
  onObservation?: (id: string) => void;
  onAudit?: (id: string) => void;
}

const Table = ({
  rows,
  isViewTypeTable,
  tableRef,
  tableClassNames,
  columns,
  columnLabels,
  columnsDetails,
  sortKey,
  sortDirection,
  handleSortChange,
  onActiveRowChange,
  onEdit,
  onDelete,
  onGetFile,
  onDeleteFile,
  onObservation,
  onAudit,
  isLoading = false,
  selection,
}: Props) => {
  const t = useT();

  const renderCellContent = (row: Vehicle, col: string, item: React.ReactNode) => {
    if (col === "driver") {
      return <div className="text-capitalize">{renderDriverContent(row.driver)}</div>;
    }

    if (col === "status" && row.status) {
      return <span className={STATUS_BADGE_CLASS[row.status]}>{t(`expense.status.${row.status}`)}</span>;
    }

    if (col === "category") {
      return (
        <SharedCategoryCell
          content={item}
          recurrenceId={row.recurrenceId}
          installmentNumber={row.installmentNumber}
          totalInstallments={row.totalInstallments}
        />
      );
    }

    return <span>{item}</span>;
  };

  return (
    <SharedTable<Vehicle>
      rows={rows}
      isViewTypeTable={isViewTypeTable}
      tableRef={tableRef}
      tableClassNames={tableClassNames}
      columns={columns}
      columnsDetails={columnsDetails}
      columnLabels={columnLabels}
      sortBy={resolveDisplaySortKey(sortKey)}
      sortDir={sortKey ? sortDirection : undefined}
      handleSortChange={handleSortChange}
      onActiveRowChange={onActiveRowChange}
      notFoundMessage={t("expense.table.notFound")}
      containerClassName="table-responsive min-height-300"
      isLoading={isLoading}
      selection={selection}
      renderCell={(row, col) => renderCellContent(row, col, row[col as keyof Vehicle] ?? <NoData text="-" />)}
      renderRowActions={(row) => (
        <td className="table-action">
          <DummyRowDropdown
            items={[
              {
                icon: "rioglyph rioglyph-download",
                onClick: () => onGetFile?.(row.id, row.category),
                title: t("expense.form.action.downloadReceipt"),
              },
              {
                icon: "rioglyph rioglyph-trash ",
                onClick: () => onDeleteFile?.(row.id),
                title: t("expense.form.action.deleteReceipt"),
              },
              {
                icon: "rioglyph rioglyph-time",
                onClick: () => onAudit?.(row.id),
                title: t("common.action.audit"),
              },
              {
                icon: "rioglyph rioglyph-route",
                onClick: () => openTripDetails({ tripId: row.tripId, driverId: row.tripDriverId }),
                title: t("common.action.viewTripDetails"),
                disabled: !buildTripDetailsUrl({ tripId: row.tripId, driverId: row.tripDriverId }),
                disabledTooltip: t("common.action.viewTripDetails.disabled"),
              },
              {
                icon: "rioglyph rioglyph-pencil",
                onClick: () => onEdit?.(row.id),
                title: t("common.action.edit"),
              },
              {
                icon: "rioglyph rioglyph-trash",
                onClick: () => onDelete?.(row.id),
                title: t("common.action.delete"),
              },
            ]}
          />
        </td>
      )}
    />
  );
};

export default Table;
